// src/components/security/SecurityRange.tsx
import React, { memo, useEffect, useMemo, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Security } from '../../proto/generated/security'
import RangeBar, { RangeBarHandle, RangeBarSkeleton, RangeBarStaticProps } from './RangeBar'
import SubtleShimmer from '../common/SubtleShimmer'
import { getSecurityKey } from '../../util/security'
import store, { RootState } from '../../store.ts'
import { OverviewWidgetResponse_FieldGroup } from '../../proto/generated/overview-widget'
import { getCandles, getOrderBook } from '../../services/apis/candles'
import { TimeBucket_TimeSeries } from '../../proto/generated/common'
import { updateOrderBooks } from '../../reducer/security.reducer'
import websocketActions from '../../actions/websocket.actions'
import { selectTradingCalendarDataByExchange } from '../../selectors/chart'
import { constructCalendarDataLookup, isTradingHoliday } from '../../util/chart'
import { formatDateToISODate } from '../../util/general'
import { OrderBook } from '../../proto/generated/security'
import { Snapshot } from '../../reducer/security.reducer'
import { findFieldByBaseCode } from '../../util/overviewFields'

interface SecurityRangeProps {
    security: Security
    show52WkRange?: boolean
    hideDayLabels?: boolean
    /** When provided, used for 52-week range instead of live overview store (avoids screener switch flicker). */
    fieldGroups?: OverviewWidgetResponse_FieldGroup[]
}

interface CandleData {
    open: number | null
    high: number | null
    low: number | null
    close: number | null
    isFromToday: boolean
}

type RangeBarBounds = {
    ltp: number
    open: number
    dayLow: number
    dayHigh: number
    weekLow?: number
    weekHigh?: number
}

const orderBookBoundsEqual = (a?: Pick<OrderBook, 'open' | 'high' | 'low' | 'close'>, b?: Pick<OrderBook, 'open' | 'high' | 'low' | 'close'>) =>
    a === b ||
    (a?.open === b?.open && a?.high === b?.high && a?.low === b?.low && a?.close === b?.close)

const extract52WeekRange = (fieldGroups: OverviewWidgetResponse_FieldGroup[] | undefined) => {
    if (!fieldGroups?.length) return { weekLow: null as number | null, weekHigh: null as number | null }
    const low = findFieldByBaseCode(fieldGroups, 'fiftyTwoWeekLow')?.value
    const high = findFieldByBaseCode(fieldGroups, 'fiftyTwoWeekHigh')?.value
    return {
        weekLow: low ? parseFloat(low) : null,
        weekHigh: high ? parseFloat(high) : null
    }
}

const computeRangeBarBounds = ({
    security,
    orderBook,
    snapshot,
    candleData,
    weekLow,
    weekHigh,
    isHoliday,
    hasValidOrderBookData
}: {
    security: Security
    orderBook?: OrderBook
    snapshot?: Snapshot
    candleData: CandleData
    weekLow: number | null
    weekHigh: number | null
    isHoliday: boolean
    hasValidOrderBookData: boolean
}): RangeBarBounds | null => {
    const multiplier = security.priceMultiplier ?? 100

    if (hasValidOrderBookData && !isHoliday && orderBook) {
        const ltp = orderBook.lastTradedPrice ? orderBook.lastTradedPrice / multiplier : orderBook.close ? orderBook.close / multiplier : 0
        return {
            ltp,
            open: (orderBook.open ?? 0) / multiplier,
            dayLow: (orderBook.low ?? 0) / multiplier,
            dayHigh: (orderBook.high ?? 0) / multiplier,
            weekLow: weekLow ?? undefined,
            weekHigh: weekHigh ?? undefined
        }
    }

    if (!hasValidOrderBookData && !isHoliday && snapshot && candleData.close !== null && candleData.isFromToday) {
        const ltp = snapshot.lastTradedPrice ? snapshot.lastTradedPrice / multiplier : 0
        return {
            ltp,
            open: candleData.open ?? 0,
            dayLow: candleData.low ?? 0,
            dayHigh: candleData.high ?? 0,
            weekLow: weekLow ?? undefined,
            weekHigh: weekHigh ?? undefined
        }
    }

    if (candleData.close !== null && candleData.isFromToday && !isHoliday) {
        return {
            ltp: candleData.close,
            open: candleData.open ?? 0,
            dayLow: candleData.low ?? 0,
            dayHigh: candleData.high ?? 0,
            weekLow: weekLow ?? undefined,
            weekHigh: weekHigh ?? undefined
        }
    }

    if (orderBook && isHoliday && candleData.close !== null) {
        const ltp = orderBook.close ? orderBook.close / multiplier : candleData.close
        return {
            ltp,
            open: candleData.open ?? 0,
            dayLow: candleData.low ?? 0,
            dayHigh: candleData.high ?? 0,
            weekLow: weekLow ?? undefined,
            weekHigh: weekHigh ?? undefined
        }
    }

    if (candleData.close !== null && candleData.isFromToday) {
        return {
            ltp: candleData.close,
            open: candleData.open ?? 0,
            dayLow: candleData.low ?? 0,
            dayHigh: candleData.high ?? 0,
            weekLow: weekLow ?? undefined,
            weekHigh: weekHigh ?? undefined
        }
    }

    // Final fallback: show the most recent available daily candle even if it isn't from today
    // (e.g. outside market hours / holidays / when no live order book is streaming), so the
    // Day's Range bar stays visible instead of disappearing entirely.
    if (candleData.close !== null) {
        return {
            ltp: candleData.close,
            open: candleData.open ?? 0,
            dayLow: candleData.low ?? 0,
            dayHigh: candleData.high ?? 0,
            weekLow: weekLow ?? undefined,
            weekHigh: weekHigh ?? undefined
        }
    }

    return null
}

const readLtpFromStore = (securityKey: string, security: Security, hasValidOrderBookData: boolean, isHoliday: boolean): number | null => {
    const state = store.getState()
    const orderBook = state.securityStore.orderBooks[securityKey]
    const snapshot = state.securityStore.snapshots[securityKey] as Snapshot | undefined
    const multiplier = security.priceMultiplier ?? 100

    if (hasValidOrderBookData && !isHoliday && orderBook) {
        if (orderBook.lastTradedPrice) return orderBook.lastTradedPrice / multiplier
        if (orderBook.close) return orderBook.close / multiplier
        return 0
    }
    if (!hasValidOrderBookData && snapshot?.lastTradedPrice) {
        return snapshot.lastTradedPrice / multiplier
    }
    return null
}

const SecurityRange: React.FC<SecurityRangeProps> = ({ security, show52WkRange = false, hideDayLabels = false, fieldGroups: fieldGroupsProp }) => {
    const dispatch = useDispatch()
    const fieldGroupsFromStore = useSelector(
        (state: RootState) => state.overviewStore.fieldGroups,
        (a, b) => a === b
    )
    const fieldGroups = fieldGroupsProp ?? fieldGroupsFromStore
    const securityKey = useMemo(() => (security ? getSecurityKey(security.exchange, security.code) : ''), [security])
    const orderBookBounds = useSelector(
        (state: RootState) => {
            const ob = securityKey ? state.securityStore.orderBooks[securityKey] : undefined
            if (!ob) return undefined
            return { open: ob.open, high: ob.high, low: ob.low, close: ob.close }
        },
        orderBookBoundsEqual
    )
    const tradingCalendarData = useSelector(selectTradingCalendarDataByExchange(security.exchange))
    const lastRenderedBoundsRef = useRef<RangeBarBounds | null>(null)
    const rangeBarRef = useRef<RangeBarHandle | null>(null)
    const lastLtpRef = useRef<number | null>(null)

    const [candleData, setCandleData] = React.useState<CandleData>({
        open: null,
        high: null,
        low: null,
        close: null,
        isFromToday: false
    })
    // The daily candle is the last fallback for bounds; until that request settles the bar is
    // still loading and holds its space, so the rows below don't jump when it appears.
    const [candleSettled, setCandleSettled] = React.useState(false)

    useEffect(() => {
        setCandleData({
            open: null,
            high: null,
            low: null,
            close: null,
            isFromToday: false
        })
        setCandleSettled(false)
    }, [securityKey])

    const hasValidOrderBookData = useSelector((state: RootState) => {
        const ob = securityKey ? state.securityStore.orderBooks[securityKey] : undefined
        if (!ob) return false
        return (
            (ob.lastTradedPrice ?? 0) > 0 ||
            (ob.open ?? 0) > 0 ||
            (ob.high ?? 0) > 0 ||
            (ob.low ?? 0) > 0
        )
    }, (a, b) => a === b)

    const calendarDataLookup = useMemo(() => {
        if (!tradingCalendarData) return null
        return constructCalendarDataLookup(tradingCalendarData, security)
    }, [tradingCalendarData, security?.exchange, security?.segment, security?.partOfCAS])

    const isHoliday = useMemo(() => {
        if (!calendarDataLookup) return false
        const today = new Date()
        const todayISODate = formatDateToISODate(today, '')
        return isTradingHoliday(todayISODate, calendarDataLookup)
    }, [calendarDataLookup])

    const { weekLow, weekHigh } = useMemo(() => extract52WeekRange(fieldGroups), [fieldGroups])

    useEffect(() => {
        if (!security) return

        const subscriptions: any = {
            orderBookSubscriptions: [
                {
                    tag: `securityRange_${security.code}`,
                    securities: [{ exchange: security.exchange, code: security.code }],
                    topBidAskOnly: false
                }
            ]
        }

        if (!hasValidOrderBookData) {
            subscriptions.securitySnapshotSubscriptions = [
                {
                    tag: `securityRangeSnapshot_${security.code}`,
                    securities: [{ exchange: security.exchange, code: security.code }]
                }
            ]
        }

        websocketActions.setWebsocketSubscribeList(dispatch, subscriptions)

        return () => {
            websocketActions.setWebsocketSubscribeList(dispatch, {
                orderBookSubscriptions: [
                    {
                        tag: `securityRange_${security.code}`,
                        securities: [],
                        topBidAskOnly: false
                    }
                ],
                securitySnapshotSubscriptions: [
                    {
                        tag: `securityRangeSnapshot_${security.code}`,
                        securities: []
                    }
                ]
            })
        }
    }, [security, dispatch, hasValidOrderBookData])

    useEffect(() => {
        if (!orderBookBounds && security) {
            getOrderBook({ security, dispatch })
                .then((result) => {
                    const orderBookData = { ...result }
                    if (!orderBookData.security) {
                        orderBookData.security = { ...security }
                    }
                    dispatch(updateOrderBooks([orderBookData]))
                })
                .catch((error) => {
                    console.error('Error fetching order book:', error)
                })
        }
    }, [orderBookBounds, security, dispatch])

    useEffect(() => {
        let mounted = true

        const fetchDayCandle = async () => {
            if (!security) return

            if (!orderBookBounds || isHoliday || !hasValidOrderBookData) {
                const referenceDate = new Date()
                referenceDate.setHours(23, 59, 59)

                try {
                    const resp = await getCandles({
                        body: {
                            security: { code: security.code, exchange: security.exchange },
                            timeBucket: { timeSeries: TimeBucket_TimeSeries.DAY, value: 1 },
                            referenceTime: referenceDate.getTime(),
                            forwardCandleCount: 0,
                            backwardCandleCount: 1,
                            dontGiveExtraCandles: true,
                            dontApplyCorporateActionAdjustments: false
                        }
                    })

                    const candle = resp?.backwardCandles?.[0]
                    if (mounted && candle) {
                        const multiplier = security.priceMultiplier ?? 100
                        const candleDate = new Date(candle.startTimestamp || 0)
                        const today = new Date()
                        const isCandleFromToday = candleDate.toDateString() === today.toDateString()

                        setCandleData({
                            open: (candle.open ?? 0) / multiplier,
                            high: (candle.high ?? 0) / multiplier,
                            low: (candle.low ?? 0) / multiplier,
                            close: (candle.close ?? 0) / multiplier,
                            isFromToday: isCandleFromToday
                        })
                    }
                } catch (error) {
                    console.error('Error fetching daily candle:', error)
                }
                if (mounted) setCandleSettled(true)
            }
        }

        fetchDayCandle()
        return () => {
            mounted = false
        }
    }, [security, orderBookBounds, isHoliday, hasValidOrderBookData])

    const bounds = useMemo(() => {
        const orderBook = securityKey ? store.getState().securityStore.orderBooks[securityKey] : undefined
        const snapshot = securityKey ? (store.getState().securityStore.snapshots[securityKey] as Snapshot | undefined) : undefined
        return computeRangeBarBounds({
            security,
            orderBook,
            snapshot,
            candleData,
            weekLow,
            weekHigh,
            isHoliday,
            hasValidOrderBookData: !!hasValidOrderBookData
        })
    }, [security, securityKey, orderBookBounds, candleData, weekLow, weekHigh, isHoliday, hasValidOrderBookData])

    const displayBounds = bounds ?? lastRenderedBoundsRef.current

    useEffect(() => {
        if (bounds) {
            lastRenderedBoundsRef.current = bounds
            lastLtpRef.current = bounds.ltp
        }
    }, [bounds])

    useEffect(() => {
        if (!securityKey || !displayBounds) return

        const syncLtp = () => {
            const ltp = readLtpFromStore(securityKey, security, !!hasValidOrderBookData, isHoliday)
            if (ltp == null || !Number.isFinite(ltp) || ltp === lastLtpRef.current) return
            lastLtpRef.current = ltp
            rangeBarRef.current?.setLtp(ltp)
        }

        syncLtp()
        return store.subscribe(syncLtp)
    }, [securityKey, security, hasValidOrderBookData, isHoliday, displayBounds])

    const has52WeekData = weekLow != null && weekHigh != null
    const shouldShow52WkRange = show52WkRange === true && has52WeekData

    if (!displayBounds) {
        if (candleSettled) return null
        return (
            <SubtleShimmer loading>
                <RangeBarSkeleton show52WkRange={shouldShow52WkRange} hideDayLabels={hideDayLabels} />
            </SubtleShimmer>
        )
    }

    const rangeBarProps: RangeBarStaticProps = {
        open: displayBounds.open,
        dayLow: displayBounds.dayLow,
        dayHigh: displayBounds.dayHigh,
        weekLow: displayBounds.weekLow ?? 0,
        weekHigh: displayBounds.weekHigh ?? 0,
        show52WkRange: shouldShow52WkRange,
        hideDayLabels,
        initialLtp: displayBounds.ltp
    }

    return <RangeBar ref={rangeBarRef} {...rangeBarProps} />
}

const securityRangePropsAreEqual = (prev: SecurityRangeProps, next: SecurityRangeProps) =>
    prev.show52WkRange === next.show52WkRange &&
    prev.hideDayLabels === next.hideDayLabels &&
    prev.fieldGroups === next.fieldGroups &&
    prev.security?.exchange === next.security?.exchange &&
    prev.security?.code === next.security?.code

export default memo(SecurityRange, securityRangePropsAreEqual)
